/*
 *  Use this to get (and track) the following properties of a DOM element:
 *        x, y, width, height, top, right, bottom, left
 *
 *  @returns [ref, elementMeasurements, forceRefresh]
 *  Make sure to assign `ref` as the ref prop for the element you want to measure.
 */
import {ResizeObserver} from '@juggle/resize-observer';
import {last} from 'lodash';
import {useEffect, useMemo, useState} from 'react';
import useMeasure, {Options as UseMeasureOptions, RectReadOnly} from 'react-use-measure';

import {useInitialEffect} from '../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler, HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';

export function useMeasureElement<T extends HTMLElement = HTMLDivElement>(
  options?: Omit<UseMeasureOptions, 'polyfill'>,
): [React.RefCallback<T>, RectReadOnly, Handler] {
  return useMeasure({
    ...options,
    polyfill: ResizeObserver,
  });
}

// Simpler/improved version of https://github.com/jaredLunde/react-hook/blob/master/packages/intersection-observer/src/index.ts
export function useIntersectionObserver(root?: HTMLElement | null): [boolean, HandlerOf<HTMLElement>] {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [isIntersecting, setIsIntersecting] = useState<boolean>(false);

  const observer = useMemo(() => {
    if (typeof IntersectionObserver === 'undefined') {
      // If IntersectionObserver isn't supported, we act as if the element was always visible.
      setIsIntersecting(true);
      return undefined;
    }

    if (!root) {
      return undefined;
    }

    return new IntersectionObserver(
      (entries) => {
        const lastEntry = last(entries);
        if (!lastEntry) {
          return;
        }

        setIsIntersecting(lastEntry.isIntersecting);
      },
      {
        root,
      },
    );
  }, [root]);

  useEffect(() => {
    if (observer && element) {
      observer.observe(element);
    }
  }, [element, observer]);

  useInitialEffect(() => () => {
    if (observer) {
      observer.disconnect();
    }
  });

  return [isIntersecting, setElement];
}
